/**
 * AppThemeConfigurator.js
 *
 * Copiado de hay-hotspot/assets/reactweb/src/components/ui/AppThemeConfigurator.js (Fase 1.6)
 * — 100% genérico, sem mudanças.
 */
import React, { Component } from 'react';
import { PrimeReactContext } from 'primereact/api';

import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Divider } from 'primereact/divider';

import { ThemeContext } from '../../context/ThemeContext';

const SCALES = [12, 13, 14, 15, 16];

const THEME_FAMILIES = [
  {
    label: 'Lara',
    items: [
      { name: 'Indigo', color: '#6366f1', light: 'lara-light-indigo', dark: 'lara-dark-indigo' },
      { name: 'Blue', color: '#3b82f6', light: 'lara-light-blue', dark: 'lara-dark-blue' },
      { name: 'Purple', color: '#a855f7', light: 'lara-light-purple', dark: 'lara-dark-purple' },
      { name: 'Teal', color: '#14b8a6', light: 'lara-light-teal', dark: 'lara-dark-teal' },
      { name: 'Cyan', color: '#06b6d4', light: 'lara-light-cyan', dark: 'lara-dark-cyan' },
      { name: 'Green', color: '#22c55e', light: 'lara-light-green', dark: 'lara-dark-green' },
      { name: 'Pink', color: '#ec4899', light: 'lara-light-pink', dark: 'lara-dark-pink' },
      { name: 'Amber', color: '#f59e0b', light: 'lara-light-amber', dark: 'lara-dark-amber' },
    ],
  },
  {
    label: 'Bootstrap 4',
    items: [
      { name: 'Blue', color: '#0d6efd', light: 'bootstrap4-light-blue', dark: 'bootstrap4-dark-blue' },
      { name: 'Purple', color: '#6f42c1', light: 'bootstrap4-light-purple', dark: 'bootstrap4-dark-purple' },
    ],
  },
  {
    label: 'Material Design',
    items: [
      { name: 'Indigo', color: '#3f51b5', light: 'md-light-indigo', dark: 'md-dark-indigo' },
      { name: 'Deep Purple', color: '#673ab7', light: 'md-light-deeppurple', dark: 'md-dark-deeppurple' },
    ],
  },
  {
    label: 'Material Compact',
    items: [
      { name: 'Indigo', color: '#3f51b5', light: 'mdc-light-indigo', dark: 'mdc-dark-indigo' },
      { name: 'Deep Purple', color: '#673ab7', light: 'mdc-light-deeppurple', dark: 'mdc-dark-deeppurple' },
    ],
  },
  {
    label: 'Soho',
    items: [
      { name: 'Soho', color: '#0d89ec', light: 'soho-light', dark: 'soho-dark' },
    ],
  },
  {
    label: 'Viva',
    items: [
      { name: 'Viva', color: '#3d67e0', light: 'viva-light', dark: 'viva-dark' },
    ],
  },
];

const SOLO_LIGHT = [
  { name: 'Tailwind', color: '#06b6d4', theme: 'tailwind-light' },
  { name: 'Fluent', color: '#0078d4', theme: 'fluent-light' },
  { name: 'Nano', color: '#007bff', theme: 'nano' },
  { name: 'Mira', color: '#8b5cf6', theme: 'mira' },
  { name: 'Rhea', color: '#059669', theme: 'rhea' },
  { name: 'Saga Blue', color: '#007bff', theme: 'saga-blue' },
  { name: 'Saga Green', color: '#22c55e', theme: 'saga-green' },
  { name: 'Saga Orange', color: '#f97316', theme: 'saga-orange' },
  { name: 'Saga Purple', color: '#a855f7', theme: 'saga-purple' },
  { name: 'Nova', color: '#4e8ef7', theme: 'nova' },
  { name: 'Nova Alt', color: '#34b5e0', theme: 'nova-alt' },
  { name: 'Nova Accent', color: '#007bff', theme: 'nova-accent' },
];

const SOLO_DARK = [
  { name: 'Vela Blue', color: '#3b82f6', theme: 'vela-blue' },
  { name: 'Vela Green', color: '#22c55e', theme: 'vela-green' },
  { name: 'Vela Orange', color: '#f97316', theme: 'vela-orange' },
  { name: 'Vela Purple', color: '#a855f7', theme: 'vela-purple' },
  { name: 'Arya Blue', color: '#4fc3f7', theme: 'arya-blue' },
  { name: 'Arya Green', color: '#66bb6a', theme: 'arya-green' },
  { name: 'Arya Orange', color: '#ffa726', theme: 'arya-orange' },
  { name: 'Arya Purple', color: '#ce93d8', theme: 'arya-purple' },
  { name: 'Luna Amber', color: '#f59e0b', theme: 'luna-amber' },
  { name: 'Luna Blue', color: '#3b82f6', theme: 'luna-blue' },
  { name: 'Luna Green', color: '#22c55e', theme: 'luna-green' },
  { name: 'Luna Pink', color: '#ec4899', theme: 'luna-pink' },
];

function findCounterpart(themeName, wantDark) {
  for (const family of THEME_FAMILIES) {
    for (const item of family.items) {
      if (item.light === themeName || item.dark === themeName) {
        return wantDark ? item.dark : item.light;
      }
    }
  }
  return null;
}

class ColorSwatch extends Component {
  render() {
    const { color, name, selected, onClick } = this.props;
    return (
      <button
        title={name}
        onClick={onClick}
        style={{
          width: '2rem',
          height: '2rem',
          borderRadius: '50%',
          background: color,
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: selected
            ? `0 0 0 2px #fff, 0 0 0 4px ${color}`
            : 'none',
          transform: selected ? 'scale(1.15)' : 'scale(1)',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
      >
        {selected && <i className="pi pi-check" style={{ color: '#fff', fontSize: '0.6rem', fontWeight: '900' }} />}
      </button>
    );
  }
}

class SectionLabel extends Component {
  render() {
    return (
      <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}
        className="text-500 mb-2 block">
        {this.props.children}
      </span>
    );
  }
}

class AppThemeConfigurator extends Component {

  renderContent(themeCtx, primeCtx) {
    const { visible, onHide } = this.props;
    const { scale, setScale, ripple, setRipple, inputStyle, setInputStyle, darkMode, setDarkMode, currentTheme, setCurrentTheme } = themeCtx;
    const { changeTheme } = primeCtx;

    const applyTheme = (newTheme) => {
      if (!newTheme || newTheme === currentTheme) return;
      changeTheme(currentTheme, newTheme, 'theme-link', () => setCurrentTheme(newTheme));
    };

    const handleFamilyClick = (item) => {
      applyTheme(darkMode ? item.dark : item.light);
    };

    const handleDarkModeToggle = (newDark) => {
      setDarkMode(newDark);
      const counterpart = findCounterpart(currentTheme, newDark);
      if (counterpart) applyTheme(counterpart);
    };

    const header = (
      <div className="flex align-items-center gap-2">
        <i className="pi pi-palette text-primary" style={{ fontSize: '1.1rem' }} />
        <span className="font-bold text-lg">Configurações de Tema</span>
      </div>
    );

    return (
      <Sidebar
        visible={visible}
        onHide={onHide}
        position="right"
        header={header}
        style={{ width: '22rem' }}
      >
        <div className="flex flex-column gap-4 pb-4">

          <section>
            <SectionLabel>Escala</SectionLabel>
            <div className="flex align-items-end gap-2">
              {SCALES.map(s => {
                const active = s === scale;
                return (
                  <button key={s} title={`${s}px`} onClick={() => setScale(s)} style={{
                    width: '2.25rem', height: '2.25rem', borderRadius: '50%', padding: 0,
                    border: `1px solid ${active ? 'var(--primary-color)' : 'var(--surface-border)'}`,
                    background: active ? 'var(--primary-color)' : 'transparent',
                    color: active ? '#fff' : 'var(--text-color)',
                    cursor: 'pointer', fontWeight: 700, fontSize: `${s}px`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s', flexShrink: 0,
                  }}>
                    A
                  </button>
                );
              })}
            </div>
          </section>

          <Divider className="my-0" />

          <section>
            <SectionLabel>Estilo do Input</SectionLabel>
            <div className="flex gap-2">
              {[
                { key: 'outlined', label: 'Contornado' },
                { key: 'filled', label: 'Preenchido' },
              ].map(({ key, label }) => {
                const active = inputStyle === key;
                return (
                  <button key={key} onClick={() => setInputStyle(key)} style={{
                    flex: 1, padding: '0.6rem 0.5rem', cursor: 'pointer',
                    border: `2px solid ${active ? 'var(--primary-color)' : 'var(--surface-border)'}`,
                    borderRadius: 'var(--border-radius)',
                    background: active ? 'var(--primary-50, #eef2ff)' : 'transparent',
                    color: active ? 'var(--primary-color)' : 'var(--text-color)',
                    fontWeight: active ? 600 : 400,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem',
                    transition: 'all 0.2s',
                  }}>
                    {key === 'outlined'
                      ? <span style={{ width: '3rem', height: '1.1rem', border: '1px solid currentColor', borderRadius: '4px', display: 'block' }} />
                      : <span style={{ width: '3rem', height: '1.1rem', background: 'currentColor', opacity: 0.25, borderRadius: '4px', display: 'block' }} />
                    }
                    <span style={{ fontSize: '0.75rem' }}>{label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <Divider className="my-0" />

          <section>
            <div className="flex align-items-center justify-content-between">
              <div>
                <SectionLabel>Efeito Ripple</SectionLabel>
                <span className="text-xs text-500">Animação ao clicar nos elementos</span>
              </div>
              <InputSwitch checked={ripple} onChange={e => setRipple(e.value)} />
            </div>
          </section>

          <Divider className="my-0" />

          <section>
            <div className="flex align-items-center justify-content-between">
              <div>
                <SectionLabel>Modo Escuro</SectionLabel>
                <span className="text-xs text-500">
                  {darkMode ? '🌙 Tema escuro ativo' : '☀️ Tema claro ativo'}
                </span>
              </div>
              <InputSwitch checked={darkMode} onChange={e => handleDarkModeToggle(e.value)} />
            </div>
          </section>

          <Divider className="my-0" />

          <section>
            <SectionLabel>Tema</SectionLabel>
            <div className="flex flex-column gap-4">
              {THEME_FAMILIES.map(family => (
                <div key={family.label}>
                  <span className="text-sm font-medium text-700 mb-2 block">{family.label}</span>
                  <div className="flex flex-wrap gap-2">
                    {family.items.map(item => (
                      <ColorSwatch
                        key={item.name}
                        name={item.name}
                        color={item.color}
                        selected={currentTheme === item.light || currentTheme === item.dark}
                        onClick={() => handleFamilyClick(item)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <Divider className="my-0" />

          <section>
            <span className="text-sm font-medium text-700 mb-2 block">Temas Específicos</span>
            <div className="flex flex-wrap gap-2">
              {(darkMode ? SOLO_DARK : SOLO_LIGHT).map(item => (
                <ColorSwatch
                  key={item.theme}
                  name={item.name}
                  color={item.color}
                  selected={currentTheme === item.theme}
                  onClick={() => applyTheme(item.theme)}
                />
              ))}
            </div>
          </section>

        </div>
      </Sidebar>
    );
  }

  render() {
    return (
      <ThemeContext.Consumer>
        {(themeCtx) => (
          <PrimeReactContext.Consumer>
            {(primeCtx) => this.renderContent(themeCtx, primeCtx)}
          </PrimeReactContext.Consumer>
        )}
      </ThemeContext.Consumer>
    );
  }
}

export default AppThemeConfigurator;
